'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';

export interface LogEntry {
  id: string;
  timestamp: number;
  type: 'trick' | 'crash' | 'combo' | 'jump' | 'land' | 'speed' | 'system' | 'ramp';
  message: string;
  points?: number;
  multiplier?: number;
}

interface GameLoggerProps {
  visible: boolean;
  onToggle: () => void;
}

// Global log bus — Phaser scene can push events here
const logListeners: Array<(entry: LogEntry) => void> = [];

export function pushGameLog(entry: Omit<LogEntry, 'id' | 'timestamp'>) {
  const full: LogEntry = {
    ...entry,
    id: `log-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    timestamp: Date.now(),
  };
  logListeners.forEach(fn => fn(full));
}

const TYPE_COLORS: Record<LogEntry['type'], string> = {
  trick: '#ffd60a',
  crash: '#ff4d6d',
  combo: '#00e5ff',
  jump: '#a8dadc',
  land: '#90e0ef',
  speed: '#48cae4',
  system: '#556677',
  ramp: '#f4a261',
};

const TYPE_ICONS: Record<LogEntry['type'], string> = {
  trick: '🛹',
  crash: '💥',
  combo: '⚡',
  jump: '↑',
  land: '↓',
  speed: '⚡',
  system: '●',
  ramp: '⟋',
};

export default function GameLogger({ visible, onToggle }: GameLoggerProps) {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  const addEntry = useCallback((entry: LogEntry) => {
    setEntries(prev => {
      const next = [...prev, entry];
      return next.length > 60 ? next.slice(-60) : next;
    });
  }, []);

  useEffect(() => {
    logListeners.push(addEntry);
    // System boot message
    pushGameLog({ type: 'system', message: 'Game engine initialized — Italy track loaded' });
    return () => {
      const idx = logListeners.indexOf(addEntry);
      if (idx !== -1) logListeners.splice(idx, 1);
    };
  }, [addEntry]);

  // Auto-scroll to bottom when new entries arrive
  useEffect(() => {
    if (autoScrollRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries]);

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return `${d.getMinutes().toString().padStart(2, '0')}:${d.getSeconds().toString().padStart(2, '0')}.${Math.floor(d.getMilliseconds() / 10).toString().padStart(2, '0')}`;
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="pointer-events-auto absolute z-30"
        style={{
          bottom: '190px',
          left: '12px',
          background: 'rgba(13,13,15,0.85)',
          border: '1px solid rgba(0,229,255,0.25)',
          borderRadius: '10px',
          padding: '6px 10px',
          color: '#00e5ff',
          fontSize: '10px',
          fontFamily: 'monospace',
          letterSpacing: '0.08em',
          backdropFilter: 'blur(8px)',
        }}
        aria-label="Toggle game log"
      >
        {visible ? '✕ LOG' : '📋 LOG'}
      </button>

      {/* Log panel */}
      {visible && (
        <div
          className="pointer-events-auto absolute z-30"
          style={{
            bottom: '230px',
            left: '12px',
            width: '260px',
            maxHeight: '220px',
            background: 'rgba(8,10,14,0.92)',
            border: '1px solid rgba(0,229,255,0.2)',
            borderRadius: '12px',
            backdropFilter: 'blur(12px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '6px 10px',
              borderBottom: '1px solid rgba(0,229,255,0.12)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ color: '#00e5ff', fontSize: '9px', fontFamily: 'monospace', letterSpacing: '0.12em' }}>
              GAME LOG — {entries.length} events
            </span>
            <button
              onClick={() => setEntries([])}
              style={{ color: '#556677', fontSize: '9px', fontFamily: 'monospace', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              CLEAR
            </button>
          </div>

          {/* Entries */}
          <div
            ref={scrollRef}
            onScroll={e => {
              const el = e.currentTarget;
              autoScrollRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
            }}
            style={{
              overflowY: 'auto',
              flex: 1,
              padding: '4px 0',
            }}
          >
            {entries.length === 0 && (
              <p style={{ color: '#334455', fontSize: '9px', fontFamily: 'monospace', padding: '8px 10px' }}>
                Waiting for events…
              </p>
            )}
            {entries.map(entry => (
              <div
                key={entry.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '6px',
                  padding: '2px 10px',
                  borderBottom: '1px solid rgba(255,255,255,0.03)',
                }}
              >
                <span style={{ color: '#334455', fontSize: '8px', fontFamily: 'monospace', minWidth: '44px', paddingTop: '1px' }}>
                  {formatTime(entry.timestamp)}
                </span>
                <span style={{ fontSize: '10px', minWidth: '14px' }}>
                  {TYPE_ICONS[entry.type]}
                </span>
                <span
                  style={{
                    color: TYPE_COLORS[entry.type],
                    fontSize: '9px',
                    fontFamily: 'monospace',
                    lineHeight: '1.4',
                    flex: 1,
                  }}
                >
                  {entry.message}
                  {entry.points !== undefined && entry.points > 0 && (
                    <span style={{ color: '#ffd60a', marginLeft: '4px' }}>
                      +{entry.points.toLocaleString()}
                      {entry.multiplier && entry.multiplier > 1 ? ` ×${entry.multiplier}` : ''}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
